import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { onboardingApi } from '../../services/api/onboarding';

const questions = [
  {
    id: 'category',
    question: 'What category does your product belong to?',
    type: 'select',
    options: [
      'Apparel and Accessories',
      'Electronics and Gadgets',
      'Home and Kitchen',
      'Health and Personal Care',
      'Beauty and Cosmetics',
      'Automotive Parts and Accessories',
      'Food and Beverages',
      'Toys and Hobbies',
      'Books, Music, and Media',
      'Sports and Outdoors',
      'Office Supplies and Equipment',
      'Pet Supplies',
      'Tools and Home Improvement',
      'Garden and Outdoor Living',
      'Baby Products',
      'Other'
    ]
  },
  {
    id: 'ingredients',
    question: 'Does your product contain any of the following ingredients?',
    type: 'multiselect',
    options: [
      'Alcohol',
      'Animal Products',
      'Chemicals',
      'Pharmaceuticals',
      'None of the above'
    ]
  },
  {
    id: 'certifications',
    question: 'Do you have any of these certifications?',
    type: 'multiselect',
    options: [
      'CE Mark',
      'FDA Approval',
      'Halal Certification',
      'ISO Certification',
      'None'
    ]
  },
  {
    id: 'battery',
    question: 'Does your product contain batteries?',
    type: 'radio',
    options: ['Yes', 'No']
  },
  {
    id: 'restricted',
    question: 'Does your product contain any restricted materials?',
    type: 'radio',
    options: ['Yes', 'No', 'Not Sure']
  }
];

function ProductInformation({ onNext, onPrevious }) {
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [otherCategory, setOtherCategory] = useState('');

  const handleAnswer = (questionId, value) => {
    if (questionId === 'category' && value === 'Other') {
      setAnswers(prev => ({
        ...prev,
        [questionId]: otherCategory || value
      }));
    } else {
      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
    }
  };

  const isCurrentQuestionAnswered = () => {
    const currentAnswer = answers[questions[currentQuestion].id];
    if (!currentAnswer) return false;
    
    if (questions[currentQuestion].type === 'multiselect') {
      return currentAnswer.length > 0;
    }
    
    return true;
  };

  const handleNext = () => {
    console.log('handleNext called', { currentQuestion, questionsLength: questions.length - 1 });
    if (currentQuestion < questions.length - 1) {
      console.log('Moving to next question');
      if (isCurrentQuestionAnswered()) {
        setCurrentQuestion(currentQuestion + 1);
      }
    } else if (isCurrentQuestionAnswered()) {
      console.log('Should move to next step');
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      onPrevious();
    }
  };

  const handleSubmit = async (e) => {
    // e.preventDefault();
    
    try {
      await onboardingApi.saveProduct({
        category: answers['category'],
        ingredients: answers['ingredients'],
        certifications: answers['certifications'],
        battery: answers['battery'] === 'Yes',
        restricted: answers['restricted']
      });
      toast.success('Product information saved successfully');
      onNext();
    } catch (error) {
      console.error('Failed to save product information:', error);
      toast.error(error.response?.data?.error || 'Failed to save product information');
    }
  };

  const renderQuestion = (question) => {
    switch (question.type) {
      case 'select':
        return (
          <div>
            <select
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              className="mt-4 block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">Select an option</option>
              {question.options.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>

            {/* Show input field when Other is selected */}
            {question.id === 'category' && answers[question.id] === 'Other' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Please specify your category
                </label>
                <input
                  type="text"
                  value={otherCategory}
                  onChange={(e) => {
                    setOtherCategory(e.target.value);
                    handleAnswer('category', 'Other'); // Update the answer with custom value
                  }}
                  placeholder="Enter your category"
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <div className="mt-4 space-y-2">
            {question.options.map((option) => (
              <label key={option} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={answers[question.id]?.includes(option) || false}
                  onChange={(e) => {
                    const currentAnswers = answers[question.id] || [];
                    let newAnswers;

                    if (e.target.checked) {
                      if (option === 'None of the above' || option === 'None') {
                        newAnswers = [option];
                      } else {
                        newAnswers = [...currentAnswers.filter(a => a !== 'None of the above' && a !== 'None'), option];
                      }
                    } else {
                      newAnswers = currentAnswers.filter(a => a !== option);
                    }

                    handleAnswer(question.id, newAnswers);
                  }}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className="mt-4 space-y-2">
            {question.options.map((option) => (
              <label key={option} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={answers[question.id] === option}
                  onChange={(e) => handleAnswer(question.id, e.target.value)}
                  className="border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl text-center font-bold text-gray-900 mb-8">
        Product Information
      </h1>

      <div className="bg-white rounded-xl p-8 shadow-sm">
        <div className="mb-8">
          <div className="relative">
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
              <div
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#0049ac]"
              ></div>
            </div>
            <div className="text-right text-sm text-gray-500">
              Question {currentQuestion + 1} of {questions.length}
            </div>
          </div>
        </div>

        <div className="min-h-[300px]">
          <h2 className="text-xl font-semibold text-gray-900">
            {questions[currentQuestion].question}
          </h2>
          {renderQuestion(questions[currentQuestion])}
        </div>

        <div className="flex justify-between mt-8">
          <button
            onClick={handlePrevious}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={!isCurrentQuestionAnswered()}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0049ac] rounded-md hover:bg-[#0049ac]/90 disabled:opacity-50"
          >
            {currentQuestion === questions.length - 1 ? 'Continue' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductInformation;